import { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Link,
	List,
	ListItem,
	ListItemText,
	Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import AddDialogStyle from "styles/application/AddDialogStyle";
import ColourConstants from "helpers/colourConstants";
import { ReactComponent as UploadIcon } from "assets/icons/uploadIcon.svg";
import LocalePicker from "components/Elements/LocalePicker";
import { localeOptions } from "helpers/localeOptions";
import { buildGlossaryImportPreview } from "helpers/glossaryCsvImport";

import {
	downloadGlossaryTemplate,
	importGlossaryCsv,
} from "services/ai";
import { showError } from "../../../../../redux/common/actions";

// Human-readable delimiter names, translated at render time.
const DELIMITER_LABEL_KEYS = {
	",": "label.delimiterComma",
	";": "label.delimiterSemicolon",
	"\t": "label.delimiterTab",
	"|": "label.delimiterPipe",
};

const AT = AddDialogStyle();

const findLocaleByValue = (value) =>
	localeOptions.find((opt) => opt.value === value) ?? null;

const useStyles = makeStyles()(() => ({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 5,
		marginBottom: 15,
	},
	dragContainer: {
		padding: 25,
		borderStyle: "dashed",
		borderColor: "#307AD6",
		borderWidth: 2,
		borderRadius: "11px",
		width: "100%",
	},
	dragTextContainer: {
		display: "flex",
		width: "100%",
		justifyContent: "center",
		alignItems: "center",
	},
	imgLink: {
		textDecoration: "underline",
		color: "#307AD6",
		"&:hover": {
			cursor: "pointer",
		},
	},
	uploadIcon: {
		marginRight: "2%",
		color: ColourConstants.uploaderIcon,
	},
	spinnerContainer: {
		display: "flex",
		justifyContent: "center",
	},
	dropText: {
		fontSize: "14px",
	},
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
	localeField: {
		marginBottom: 15,
	},
	templateLink: {
		cursor: "pointer",
		fontSize: "16px",
		color: "#307AD6",
	},
}));

const BulkImportDialog = ({
	open,
	closeHandler,
	onSubmit,
	defaultLocale,
	existingEntries,
	siteAppId,
}) => {
	const { classes } = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const [locale, setLocale] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [previewError, setPreviewError] = useState("");

	useEffect(() => {
		if (!open) return;
		setLocale(
			findLocaleByValue(defaultLocale) ??
				localeOptions.find((opt) => opt.language === "es") ??
				localeOptions[0] ??
				null
		);
		setUploading(false);
		setSelectedFile(null);
		setPreview(null);
		setPreviewError("");
	}, [open, defaultLocale]);

	const resetPreview = useCallback(() => {
		setSelectedFile(null);
		setPreview(null);
		setPreviewError("");
	}, []);

	const buildPreview = useCallback(
		(text) => buildGlossaryImportPreview({ text, locale, existingEntries }),
		[existingEntries, locale]
	);

	const downloadTemplate = async () => {
		const res = await downloadGlossaryTemplate();
		if (res.status && res.data) {
			const blob = new Blob([res.data], { type: "text/csv" });
			const a = document.createElement("a");
			a.href = URL.createObjectURL(blob);
			a.download = "glossary-import-template.csv";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(a.href);
		} else {
			dispatch(showError(t("message.failedToDownloadCsvTemplate")));
		}
	};

	const onDrop = useCallback(
		async (acceptedFiles) => {
			const file = acceptedFiles?.[0];
			if (!file) return;
			if (!file.name.toLowerCase().endsWith(".csv")) {
				dispatch(showError(t("message.onlyCsvAccepted")));
				return;
			}
			if (!locale) {
				dispatch(showError(t("message.pickTargetLocaleFirst")));
				return;
			}

			setUploading(true);
			try {
				const text = await file.text();
				const nextPreview = buildPreview(text);
				setSelectedFile(file);
				setPreview(nextPreview);
				setPreviewError("");
			} catch (error) {
				const message =
					error?.message || t("message.csvCouldNotBeParsed");
				setSelectedFile(null);
				setPreview(null);
				setPreviewError(message);
				dispatch(showError(message));
			} finally {
				setUploading(false);
			}
		},
		[buildPreview, locale, dispatch, t]
	);

	const confirmImport = async () => {
		if (!selectedFile || !locale) return;

		setUploading(true);
		try {
			const res = await importGlossaryCsv({
				siteAppId,
				targetLanguage: locale.value,
				file: selectedFile,
			});
			await onSubmit({
				targetLanguage: locale.value,
				response: res,
			});
		} catch (error) {
			dispatch(showError(error?.message || "Failed to import glossary CSV."));
		} finally {
			setUploading(false);
		}
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: false,
		accept: { "text/csv": [".csv"] },
		disabled: uploading || !locale,
	});

	return (
		<>
			{uploading && <LinearProgress className={classes.loading} />}
			<Dialog open={open} onClose={closeHandler} fullWidth maxWidth="md">
				<AT.ActionContainerFileUpload>
					<DialogTitle>
						<AT.HeaderText>{t("title.uploadCsvFile")}</AT.HeaderText>{" "}
						<AT.NoteText>{t("message.uploadCsvNote")}</AT.NoteText>
					</DialogTitle>
					<AT.NoteButtonContainer>
						<AT.CancelButton
							variant="contained"
							onClick={closeHandler}
							disabled={uploading}
						>
							{t("action.cancel")}
						</AT.CancelButton>
					</AT.NoteButtonContainer>
				</AT.ActionContainerFileUpload>
				<DialogContent>
					<Box className={classes.localeField}>
						<LocalePicker
							value={locale}
							onChange={(nextLocale) => {
								setLocale(nextLocale);
								resetPreview();
							}}
							label={t("label.defaultTargetLocale")}
							required
							helperText={t("message.defaultTargetLocaleHelper")}
						/>
					</Box>

					<div className={classes.content}>
						{!uploading && !preview ? (
							<div
								{...getRootProps()}
								className={classes.dragContainer}
							>
								<input {...getInputProps()} />
								{isDragActive ? (
									<div className={classes.dragTextContainer}>
										<Typography className={classes.dropText}>
											{t("message.dropFileHere")}
										</Typography>
									</div>
								) : (
									<div className={classes.dragTextContainer}>
										<UploadIcon
											alt="upload icon"
											className={classes.uploadIcon}
										/>
										<Typography className={classes.dropText}>
											{t("message.dragAndDropCsvFile")}{" "}
											<Link
												className={classes.imgLink}
												underline="none"
											>
												{t("action.browse")}
											</Link>
										</Typography>
									</div>
								)}
							</div>
						) : !uploading && preview ? (
							<>
								<Alert severity="info">
									<Typography variant="subtitle2">
										{t("message.importPreviewSummary", {
											newCount: preview.newCount,
											replacedCount: preview.replacedCount,
											importedCount: preview.importedCount,
										})}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{t("label.detectedDelimitedFile", {
											delimiter: DELIMITER_LABEL_KEYS[preview.delimiter]
												? t(DELIMITER_LABEL_KEYS[preview.delimiter])
												: preview.delimiter,
										})}
									</Typography>
								</Alert>

								<Accordion defaultExpanded>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="subtitle1">
											{t("title.importPreview")}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										{preview.newRecords.length > 0 && (
											<>
												<Typography variant="subtitle2" sx={{ mb: 1 }}>
													{t("label.newRecordsCount", {
														count: preview.newCount,
													})}
												</Typography>
												<List dense>
													{preview.newRecords.slice(0, 20).map((row, index) => (
														<ListItem key={`new-${row.targetLanguage}-${row.sourceTerm}-${index}`} disableGutters>
															<ListItemText
																primary={`${row.sourceTerm} -> ${row.targetTerm}`}
																secondary={`${row.targetLanguage}${row.context ? ` | ${row.context}` : ""}`}
															/>
														</ListItem>
													))}
												</List>
											</>
										)}

										{preview.replacedRecords.length > 0 && (
											<>
												<Typography variant="subtitle2" sx={{ mb: 1, mt: preview.newRecords.length > 0 ? 2 : 0 }}>
													{t("label.replacedRecordsCount", {
														count: preview.replacedCount,
													})}
												</Typography>
												<List dense>
													{preview.replacedRecords.slice(0, 20).map((row, index) => (
														<ListItem key={`replaced-${row.targetLanguage}-${row.sourceTerm}-${index}`} disableGutters>
															<ListItemText
																primary={`${row.sourceTerm} -> ${row.targetTerm}`}
																secondary={`${t("message.replacesTerm", { term: row.existingTargetTerm })}${row.targetLanguage ? ` | ${row.targetLanguage}` : ""}${row.context ? ` | ${row.context}` : ""}`}
															/>
														</ListItem>
													))}
												</List>
											</>
										)}
									</AccordionDetails>
								</Accordion>

								{preview.importedCount > 20 && (
									<Typography variant="caption" color="text.secondary">
										{t("message.showingFirst20Preview")}
									</Typography>
								)}
							</>
						) : (
							<div className={classes.dragContainer}>
								<div className={classes.spinnerContainer}>
									<CircularProgress />
								</div>
							</div>
						)}

						{!!previewError && (
							<Alert severity="error">{previewError}</Alert>
						)}
					</div>

					<div style={{ height: "10px" }} />
					<Link
						onClick={downloadTemplate}
						className={classes.templateLink}
					>
						{t("action.downloadCsvTemplate")}
					</Link>
					<div style={{ height: "10px" }} />
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					{preview && (
						<>
							<Button onClick={resetPreview} disabled={uploading}>
								{t("action.chooseAnotherFile")}
							</Button>
							<AT.ConfirmButton
								variant="contained"
								onClick={confirmImport}
								disabled={uploading}
							>
								{uploading
									? t("message.importingEllipsis")
									: t("action.confirmImport")}
							</AT.ConfirmButton>
						</>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BulkImportDialog;
